// Schedule's things as RowList rows (BL-189 consolidation; Who's working and Time off were TeamList and
// TimeOffList): what each row says, in words, with no drawing of its own.
// - Team manager › Time off's Company holidays.
// - Who's working (Team manager and Schedule › Map, as dispatch.board's rows give them): each
//   technician by their pen, their state now as a word (red when late), what they're on and how full the
//   day is, what they do, and the one action, their day. The one whose day shows beside the list is
//   marked and says Shown.
// - Time off (timeOff.list): who by their pen, the leave, the days or hours in words, the note, and paid
//   or not. A cancelled one stays, muted and struck through, with why; for those who may, Cancel it.
import type { Row, RowTone } from '../components/RowList/RowList.tsx';
import type { Pen } from '../components/pen.ts';
import { duration, visitsWord } from '../format.ts';
import type { TeamListProps, TimeOffListProps } from '../schedule-schemas.ts';

export interface HolidayRow {
  /** "Thu, Dec 25" */
  day: string;
  /** "Christmas Day" */
  name: string;
  paid: boolean;
}

/** A coming holiday: the day, then its name, whether it's paid, and that nobody is booked on it. */
export const holidayRows = (items: readonly HolidayRow[]): Row[] =>
  items.map((h) => ({
    id: `${h.day}-${h.name}`,
    name: h.day,
    lines: [{ text: `${h.name}${h.paid ? '' : ', unpaid'}. Nobody is booked.` }],
  }));

type Tech = TeamListProps['techs'][number];

/** A state's word, as DESIGN.md §2 draws it: muted for kind and state, dashed for waiting. */
const TECH_TONE: Record<Tech['state'], RowTone> = {
  working: 'ink',
  driving: 'ink',
  away: 'wait',
  free: 'muted',
  off: 'muted',
};

/** Who's working today, one technician a row; picking one shows their day (onPick). */
export const teamRows = (
  data: TeamListProps,
  { selected, onPick }: { selected?: string | null; onPick?: (id: string) => void } = {},
): Row[] =>
  data.techs.map((t): Row => ({
    id: t.id,
    name: t.name,
    pen: (t.pen ?? null) as Pen | null,
    word: t.late ? { text: t.late, tone: 'bad' } : { text: t.word, tone: TECH_TONE[t.state] },
    lines: [
      {
        text: `${t.line}${/\.$/.test(t.line) ? '' : '.'}${
          // a day off with nothing on it says only why
          t.state === 'off' && !t.visits
            ? ''
            : ` ${visitsWord(t.visits)}, ${duration(t.bookedMinutes)} booked.`
        }`,
      },
      ...(t.skills.length ? [{ text: `Does ${t.skills.join(', ').toLowerCase()}` }] : []),
    ],
    ...(t.id === selected
      ? { marked: true, end: { aside: 'Shown' } }
      : onPick
        ? { end: { action: 'Their day', onPress: () => onPick(t.id), label: `${t.name}’s day` } }
        : {}),
  }));

/** Time off, one row each; for those who may, cancelling one (onCancel). Without it an unpaid one says so. */
export const timeOffRows = (data: TimeOffListProps, onCancel?: (id: string) => void): Row[] =>
  data.items.map((o): Row => ({
    id: o.id,
    name: o.name,
    pen: (o.pen ?? null) as Pen | null,
    after: { text: `, ${o.label.toLowerCase()}` },
    lines: [
      { tone: 'ink', text: o.text, ...(o.cancelled ? { struck: true } : {}) },
      ...(o.note ? [{ text: o.note }] : []),
      ...(o.cancelled ? [{ text: `Cancelled: ${o.cancelled.reason}` }] : []),
    ],
    ...(o.cancelled
      ? { off: true, end: { word: 'Cancelled', tone: 'muted' } }
      : onCancel
        ? {
            end: {
              action: 'Cancel it',
              onPress: () => onCancel(o.id),
              label: `Cancel ${o.name}’s ${o.label.toLowerCase()}`,
            },
          }
        : !o.paid
          ? { end: { word: 'Unpaid', tone: 'muted' } }
          : {}),
  }));
